/*
 *  ************************************************************************
 *  *  © [2015 - 2020] Quintype Technologies India Private Limited
 *  *  All Rights Reserved.
 *  *************************************************************************
 */
import React from "react";
import get from "lodash/get";
import PropTypes from "prop-types";
import { Text } from ".././../../arrow-components/src/components/Atoms/StoryElements/Text";
import { Image } from ".././../../arrow-components/src/components/Atoms/StoryElements/Image";
import { Blurb } from ".././../../arrow-components/src/components/Atoms/StoryElements/Blurb";
import { Summary } from ".././../../arrow-components/src/components/Atoms/StoryElements/Summary";
import { Attachment } from ".././../../arrow-components/src/components/Atoms/StoryElements/Attachment";
import { QuestionAnswer } from ".././../../arrow-components/src/components/Atoms/StoryElements/QuestionAnswer";
import { BlockQuote } from ".././../../arrow-components/src/components/Atoms/StoryElements/BlockQuote";
import { AlsoRead } from ".././../../arrow-components/src/components/Atoms/StoryElements/AlsoRead";
import { Quote } from ".././../../arrow-components/src/components/Atoms/StoryElements/Quote";
import { BigFact } from ".././../../arrow-components/src/components/Atoms/StoryElements/BigFact";
import { Reference } from ".././../../arrow-components/src/components/Atoms/StoryElements/Reference";
import { ImageSlideshowElement } from "../basic/image-slideshow-element";
import { ResponsiveImage, StoryElement, SocialShare, LazyLoadImages, ImageGalleryElement } from "@quintype/components";
import { ImageLightbox } from "./lightbox/image-lightbox";
import { scrollInViewCard } from "../../../utils/scroll-in-view";
import { selectedStoryTemplate, getInfiniteScrollConfigStyle } from "../../utils.js";
import { ShareTemplate } from "../molecules/share/index";
import "./photo-story-card.module.css";

import { truncate } from "../../../utils/truncate-string";
import { CustomLink } from "../custom-link";
import {
  BlurbTemplatesMapping,
  BlockQuoteTemplatesMapping,
  BlockQuoteIconType,
  SummaryTemplatesMapping,
  QATemplatesMapping,
  QuestionTemplatesMapping,
  AnswerTemplatesMapping,
  AlsoReadTemplatesMapping,
  QuoteTemplatesMapping
} from "./story-template-map";
import "./story-card-alignment.scss";

const MainImageWrapper = ({ imageElement, story, card, socialShareGrey, primaryColor, children, cdn }) => (
  <>
    <figure styleName="image" data-testid="photo-story-image">
      <LazyLoadImages margin={"0px"}>
        <ImageLightbox
          imageSlug={imageElement["image-s3-key"]}
          cdn={cdn}
          template={ResponsiveImage}
          element={imageElement}
          key={imageElement.id}
          metadata={imageElement["metadata"]}
          slug={imageElement["image-s3-key"]}
          defaultWidth={480}
          widths={[250, 480, 640]}
          imgParams={{ auto: ["format", "compress"] }}
          alt={imageElement.title}
        />
      </LazyLoadImages>
    </figure>
    <div styleName="story-element-wrapper slideStyle">
      <div styleName="wrapper">
        {imageElement["image-attribution"] && (
          <div styleName="attribution">Credits: {imageElement["image-attribution"]}</div>
        )}
        <SocialShare
          url={`${get(story, ["slug"], "")}?cardId=${get(card, ["id"], -1)}`}
          fullUrl={story.url}
          title={
            get(card, ["metadata"], {})
              ? get(card, ["metadata", "social-share", "title"], "")
              : get(story, ["title"], "")
          }
          template={ShareTemplate}
          grey={socialShareGrey || ""}
          cardShare={true}
          hashtags={get(story, ["tags"], "")
            .map(tag => tag.name)
            .toString()}
          primaryColor={primaryColor || ""}
        />
      </div>
      {imageElement.title && (
        <figcaption dangerouslySetInnerHTML={{ __html: imageElement.title }} styleName="caption" />
      )}
      {children}
    </div>
  </>
);

MainImageWrapper.propTypes = {
  imageElement: PropTypes.object,
  children: PropTypes.node,
  socialShareGrey: PropTypes.bool,
  primaryColor: PropTypes.string,
  story: PropTypes.object,
  card: PropTypes.object,
  cdn: PropTypes.string
};

class PhotoStoryCard extends React.Component {
  constructor(props) {
    super(props);
    this.cardRef = null;
  }

  componentDidMount() {
    scrollInViewCard(this.props, this.cardRef);
  }
  getStoryElement(element, index) {
    const storyTemp = get(this.props, ["story"]);
    const pageBuilderStoryElementSetting = get(
      this.props,
      ["config", "pagebuilder-config", "story", "globalStoryElements"],
      {}
    );
    const primaryColor = get(this.props, ["config", "theme-attributes", "primary_color"]);
    const subType = element.subtype || element.type;
    switch (subType) {
      case "summary":
        const summaryTemplate = get(this.props, ["config", "theme-attributes", "story-elements", "summary"], "");
        const {
          template: summaryPBTemplate = "",
          css: { headerBgColor = "" } = {},
          opts: { headline = "", hideHeadline = "" } = {}
        } = get(pageBuilderStoryElementSetting, ["summary"], {});
        return (
          <Summary
            element={element}
            template={summaryPBTemplate || SummaryTemplatesMapping[summaryTemplate]}
            opts={{
              headline: headline || "Summary",
              hideHeadline: hideHeadline
            }}
            css={{ headerBgColor: headerBgColor || primaryColor }}
          />
        );
      case "blockquote":
        const blockQuoteTemplate = get(this.props, ["config", "theme-attributes", "story-elements", "block-quote"], "");
        const {
          template: blockQuotePBTemplate = "",
          css: { iconType = "", blockQuoteColor = "", backgroundShade = "" } = {}
        } = get(pageBuilderStoryElementSetting, ["blockquote"], {});
        return (
          <BlockQuote
            element={element}
            template={blockQuotePBTemplate || BlockQuoteTemplatesMapping[blockQuoteTemplate]}
            css={{
              iconType: iconType || BlockQuoteIconType[blockQuoteTemplate],
              blockQuoteColor: blockQuoteColor || primaryColor,
              backgroundShade: backgroundShade || primaryColor
            }}
          />
        );
      case "bigfact":
        return <BigFact element={element} />;
      case "image-gallery":
        const template = get(element, ["metadata", "type"]) === "gallery" ? ImageGalleryElement : ImageSlideshowElement;
        return (
          <div key={index}>
            <ImageLightbox
              template={template}
              element={element}
              key={element.id}
              story={this.props.story}
              cdn={this.props.cdn}
            />
          </div>
        );
      case "blurb":
        const blurbTemplate = get(this.props, ["config", "theme-attributes", "story-elements", "blurb"], "");
        const { template: blurbPBTemplate = "", css: { borderColor = "" } = {} } = get(
          pageBuilderStoryElementSetting,
          ["blurb"],
          {}
        );
        return (
          <Blurb
            element={element}
            template={blurbPBTemplate || BlurbTemplatesMapping[blurbTemplate]}
            css={{ borderColor: borderColor || primaryColor }}
          />
        );
      case "quote":
        const quoteTemplate = get(this.props, ["config", "theme-attributes", "quote-element"], "default");
        const { template: quotePBTemplate = "", css: { borderColor: quoteBorderColor = "" } = {} } = get(
          pageBuilderStoryElementSetting,
          ["quote"],
          {}
        );
        return (
          <Quote
            element={element}
            template={quotePBTemplate || QuoteTemplatesMapping[quoteTemplate]}
            css={{ borderColor: quoteBorderColor || primaryColor }}
          />
        );
      case "also-read":
        const alsoReadTemplate = get(this.props, ["config", "theme-attributes", "also-read-element"], "");
        const { template: alsoReadPBTemplate = "", css: { textColor = "" } = {}, opts: { title = "" } = {} } = get(
          pageBuilderStoryElementSetting,
          ["also-read"],
          {}
        );
        return (
          <AlsoRead
            story={storyTemp}
            element={element}
            template={alsoReadPBTemplate || AlsoReadTemplatesMapping[alsoReadTemplate]}
            css={{ textColor: textColor || primaryColor }}
            opts={{ title }}
          />
        );
      case "image":
        return <Image element={element} />;
      case "q-and-a":
        const questionAnswerTemplate = get(
          this.props,
          ["config", "theme-attributes", "story-elements", "q-and-a-element"],
          "q-and-a-element-default"
        );
        return (
          <QuestionAnswer
            element={element}
            opts={{
              type: "q-and-a",
              defaultIconType: "edge"
            }}
            template={QATemplatesMapping[questionAnswerTemplate]}
            css={{
              iconColor: primaryColor
            }}
          />
        );
      case "question":
        const questionTemplate = get(
          this.props,
          ["config", "theme-attributes", "story-elements", "question-element"],
          "question-element-default"
        );
        return (
          <QuestionAnswer
            element={element}
            opts={{
              type: "question",
              defaultIconType: "edge"
            }}
            template={QuestionTemplatesMapping[questionTemplate]}
            css={{
              iconColor: primaryColor
            }}
          />
        );
      case "answer":
        const answerTemplate = get(
          this.props,
          ["config", "theme-attributes", "story-elements", "answer-element"],
          "answer-element-default"
        );
        return (
          <QuestionAnswer
            element={element}
            opts={{
              type: "answer",
              defaultIconType: "edge"
            }}
            template={AnswerTemplatesMapping[answerTemplate]}
            css={{
              iconColor: primaryColor
            }}
          />
        );
      case "attachment":
        return <Attachment element={element} />;
      case "text":
        const { css: { textColor: textElementColor = "", hyperlinkColor = "" } = {} } = get(
          pageBuilderStoryElementSetting,
          ["text"],
          {}
        );
        return <Text element={element} css={{ textColor: textElementColor, hyperlinkColor: hyperlinkColor }} />;
      case "references":
        const { opts: { headlineText = "", showHeadline = true } = {} } = get(
          pageBuilderStoryElementSetting,
          ["references"],
          {}
        );
        return <Reference element={element} opts={{ showHeadline: showHeadline, headlineText: headlineText }} />;
      default:
        return <StoryElement element={element} key={element.id} story={storyTemp} />;
    }
  }

  getStoryElements(storyElements, config) {
    const isFirstWhiteListed = (index, key) => {
      const whiteListObject = {
        0: {
          "also-read": true,
          quote: true,
          blockquote: true
        }
      };

      return get(whiteListObject, [index, key], false);
    };
    const elementAlignment = get(
      this.props,
      ["card", "metadata", "attributes", "elementalignment", 0],
      ""
    ).toLowerCase();

    const isAlignment = elementAlignment && `align-${elementAlignment}`;
    const firstElement = get(this.props, ["card", "story-elements", 0]);
    const cardStyle =
      elementAlignment && isFirstWhiteListed(0, firstElement.subtype) ? `card--${elementAlignment}` : "";
    const infiniteScrollStyle = getInfiniteScrollConfigStyle(this.props.config);
    const firstStory = this.props.firstStory;
    const mountAt = get(config, ["mountAt"]) || "";

    if (infiniteScrollStyle === "read-more" && !firstStory) {
      return (
        <div className="element-wrapper read-more-overlay">
          {(firstElement.type === "text" || firstElement.type === "title") && (
            <div key={firstElement.id} className="read-more-card read-more-overlay">
              <bdi dangerouslySetInnerHTML={{ __html: truncate(firstElement.text, 400, true) }} />
              <div className="content-overlay" />
            </div>
          )}
          <span className="read-more-btn">
            <CustomLink href={`${mountAt}/${get(this.props, ["story", "slug"], "")}`} rel="noopener noreferrer">
              Read More
            </CustomLink>
          </span>
        </div>
      );
    } else {
      return (
        <div styleName="elements-wrapper" className={`card ${cardStyle}`}>
          {storyElements.map((element, index) => {
            const removeMaxWidth =
              elementAlignment && isFirstWhiteListed(index, element.subtype) ? "remove-margin" : "";

            if (isFirstWhiteListed(index, element.subtype)) {
              return (
                <div className={`element-wrapper ${isAlignment} ${removeMaxWidth}`}>
                  {this.getStoryElement(element, index)}
                </div>
              );
            }
            return (
              <div key={element.id} className={`element-wrapper  ${isAlignment} ${removeMaxWidth}`}>
                {this.getStoryElement(element, index)}
              </div>
            );
          })}
        </div>
      );
    }
  }

  render() {
    const { template, card, story, config } = this.props;
    const selectTemplate = selectedStoryTemplate(template, story, config);
    const [imageElement, otherElements] = card["story-elements"].reduce(
      ([imageElement, otherElements], element) => {
        if (!imageElement && element.type === "image") {
          return [element, otherElements];
        }
        return [imageElement, [...otherElements, element]];
      },
      [null, []]
    );

    return (
      <div
        ref={cardRef => (this.cardRef = cardRef)}
        styleName={imageElement && selectTemplate === "hero-priority" ? "photo-story-card" : "photo-story-card-default"}
      >
        {imageElement ? (
          <MainImageWrapper imageElement={imageElement} {...this.props}>
            {this.getStoryElements(otherElements, config)}
          </MainImageWrapper>
        ) : (
          this.getStoryElements(otherElements, config)
        )}
      </div>
    );
  }
}

PhotoStoryCard.propTypes = {
  story: PropTypes.object,
  config: PropTypes.object,
  card: PropTypes.object,
  socialShareGrey: PropTypes.bool,
  primaryColor: PropTypes.string,
  cdn: PropTypes.string,
  template: PropTypes.string,
  firstStory: PropTypes.bool
};

export { PhotoStoryCard };
